import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { BottomNav } from "@/components/BottomNav";
import { ShieldCheck, Wallet, UserRound, ArrowRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About — PataTasks" },
      {
        name: "description",
        content:
          "PataTasks is a modern app and web testing platform. Earn money by testing digital products and sharing structured feedback.",
      },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <div className="bg-background pb-20 md:pb-0">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
        <div className="flex items-center gap-3 mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
            PataTasks
          </h1>
          <span className="rounded-full border border-blue-300 bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700">
            App & Web Testing Platform
          </span>
        </div>

        <div className="space-y-6 text-base leading-relaxed text-slate-700">
          <p className="rounded-xl border border-blue-200 bg-white p-4 sm:p-5 shadow-sm">
            PataTasks is a modern app and web testing platform that enables users to earn money by
            testing digital products for third-party providers. Testers can explore a wide range of
            opportunities across categories such as music, social media, browsing, education,
            coding, e-commerce, and productivity.
          </p>

          <div className="rounded-xl border border-blue-200 bg-white p-4 sm:p-5 shadow-sm">
            <h2 className="text-lg font-bold text-foreground mb-2">What We Do</h2>
            <p>
              PataTasks connects developers, product teams, and website owners with real users who
              provide structured, high-quality feedback. Our focus is on improving usability, user
              experience (UI/UX), performance, and overall product quality.
            </p>
            <p className="mt-2">
              Each test includes clear instructions and a guided questionnaire, ensuring that
              feedback is consistent, actionable, and valuable for product improvement.
            </p>
          </div>

          <div className="rounded-xl border border-blue-200 bg-white p-4 sm:p-5 shadow-sm">
            <h2 className="text-lg font-bold text-foreground mb-2">For Testers</h2>
            <ul className="list-disc list-inside space-y-1 text-slate-700">
              <li>Browse and participate in app and web tests</li>
              <li>Provide feedback through simple, structured tasks</li>
              <li>Earn rewards for completed test submissions</li>
              <li>Access payouts through mobile money, PayPal, or bank transfer</li>
            </ul>
          </div>

          <div className="rounded-xl border border-blue-200 bg-white p-4 sm:p-5 shadow-sm">
            <h2 className="text-lg font-bold text-foreground mb-2">Payments & Transparency</h2>
            <p>
              All payments are processed securely via IntaSend. Test rewards are credited instantly
              upon completion.
            </p>
            <p className="mt-2">
              A small maintenance fee is applied to support platform operations, including database
              infrastructure, development, and ongoing system maintenance. This fee ensures
              sustainability—we do not profit directly from tester earnings.
            </p>
          </div>

          <div className="rounded-xl border border-blue-200 bg-white p-4 sm:p-5 shadow-sm">
            <h2 className="text-lg font-bold text-foreground mb-2">Our Commitment</h2>
            <p>
              PataTasks is built on transparency, fairness, and security. We are committed to
              providing:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-slate-700">
              <li>Reliable earning opportunities for testers</li>
              <li>High-quality insights for product teams</li>
              <li>Secure and efficient payment systems</li>
            </ul>
          </div>

          <div className="rounded-xl border border-blue-200 bg-white p-4 sm:p-5 shadow-sm">
            <h2 className="text-lg font-bold text-foreground mb-2">Explore your dashboard</h2>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/wallet">
                <Button className="gap-2">
                  <Wallet className="h-4 w-4" /> My Wallet
                </Button>
              </Link>
              <Link to="/profile">
                <Button variant="outline" className="gap-2">
                  <UserRound className="h-4 w-4" /> My Profile
                </Button>
              </Link>
            </div>
          </div>

          <p className="text-center text-sm text-slate-500">
            Start testing. Share your experience. Get rewarded.
          </p>
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
